import { action, computed, observable, reaction } from 'mobx';
import { IRouteDefinition, RouterStore } from '@estee/elc-router';
import { DELIVERY, INCOMPATIBLE_BROWSER, PAYMENT } from '../../routes';
import InitAppUseCase from '../../use-cases/InitAppUseCase';
import TransactionRepository from '../../repositories/TransactionRepository';
import Transaction from '../../domain/entities/Transaction/Transaction';
import Address from '../../domain/entities/Address';
import UseRecommendedUpsAddressUseCase from '../../use-cases/ups/UpsUseRecommendedAddressUseCase';
import ShippingRepository from '../../repositories/ShippingRepository';
import Session from '../../domain/entities/Session/Session';
import ConfigRepository from '../../repositories/ConfigRepository';
import TranslationRepository from '../../repositories/TranslationRepository';
import { ITranslationSchema } from '../../interfaces/ITranslationFields';
import { isCountryUS } from '../../utils/CountryUtils';
import { TIMEOUT_INTERVAL, UPS_MODAL_OPTIONS, DeliveryOptions, FIELD_TYPE } from '../../constants/Constants';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import { scrollToTargets } from '../../utils/ScrollTo';
import BillingRepository from '../../repositories/BillingRepository';
import { RESTRICTION_ERRORS, PAYMENT_ERRORS } from '../../constants/Errors';
import { emit, events } from '@estee/elc-service-bus';
import Routes, { ExternalRoute } from '../../application/routes/Routes';
import {
  paymentQueryParamsErrorsMapping,
  shippingQueryParamsErrorsMapping,
  queryParamsErrorsMapping,
} from '../../constants/QueryParams';
import { ENVIRONMENT_TYPE } from '../../helpers/getEnvironment';
import { NOTIFICATION_GROUP, NOTIFICATION_TYPE } from '../../constants/Notifications';
import { NotificationsService } from '../../application/services/NotificationsService';

export interface IAppViewControllerConfig {
  configRepository: ConfigRepository;
  stateRouter: RouterStore;
  transactionRepository: TransactionRepository;
  session: Session;
  shippingRepository: ShippingRepository;
  billingRepository: BillingRepository;
  notificationsCollection: NotificationsCollection;
  notificationsService: NotificationsService;
  routes: Routes
  translationRepository: TranslationRepository;
}

interface IAppViewControllerData {
  isCountryUS: boolean;
  isLoading: boolean;
  translationSchema: ITranslationSchema;
}

export default class AppViewController {
  private stateRouter: RouterStore;

  private transactionRepository: TransactionRepository;

  private transaction: Transaction;

  private shippingRepository: ShippingRepository;

  private billingRepository: BillingRepository;

  private session: Session;

  private configRepository: ConfigRepository;

  private translationRepository: TranslationRepository;

  private notificationService: NotificationsService;

  @observable isLoading = true;

  @observable focusBillingSection = false;

  @observable isWarningMessageActive = false;

  @observable isUPSModalActive = false;

  @observable isBillingSameAsShipping: boolean;

  @observable notificationsCollection: NotificationsCollection;

  @observable isCompanyBilling: boolean;

  @observable routes: Routes;

  constructor(config: IAppViewControllerConfig) {
    this.stateRouter = config.stateRouter;
    this.transactionRepository = config.transactionRepository;
    this.shippingRepository = config.shippingRepository;
    this.billingRepository = config.billingRepository;
    this.session = config.session;
    this.configRepository = config.configRepository;
    this.notificationsCollection = config.notificationsCollection;
    this.routes = config.routes;
    this.translationRepository = config.translationRepository;
    this.notificationService = config.notificationsService;

    reaction(() => this.notificationsCollection.notifications, () => {
      if (this.notificationsCollection.hasErrors) {
        scrollToTargets('[data-notifications]');
      }
    }, {
      delay: TIMEOUT_INTERVAL.HUNDRED,
    });
  }

  async loaded(): Promise<void> {
    this.translationRepository.loadTranslations();
    this.notificationService.initNotificationManagers();
    this.checkBrowserCompatibility();
    this.stateRouter.startRouting();

    const initAppUseCase = new InitAppUseCase();
    await initAppUseCase.execute();

    emit(events.ECOMM_STARTED);
    this.setupSession();
  
    const transaction = await this.transactionRepository.getTransaction();
    this.setTransaction(transaction);
    this.setIsLoading(false);

    if (transaction.startScreen === PAYMENT.ROUTE) {
      if (transaction.shippingAddress?.attributes[FIELD_TYPE.BAD_FIELDS]?.length) {
        if (transaction.shippingAddress.isNewAddress) {
          this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE, {});
        } else {
          this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE, {}, { inEditMode: 1 });
        }
      } else {
        this.stateRouter.changeCurrentRoute(PAYMENT.ROUTE, { isExpressFlow: true });
      }
    }

    this.collectQueryParamsErrors();

    // Check if shipping and billing addresses in payment page
    // If not, redirect to delivery page
    reaction(
      () => ({
        billingAddress: this.transaction.billingAddress,
        cartEmptyError: this.notificationsCollection.hasNotification(PAYMENT_ERRORS.CART_EMPTY),
        cartItemsCount: this.transaction.cart.cartItems.length,
        currentRoute: this.stateRouter.currentRoute,
        namedDayError: this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.NAMED_DAY_DELIVERY_RESTRICTION),
        shippingAddress: this.transaction.shippingAddress,
      }),
      ({
        cartEmptyError,
        cartItemsCount,
        currentRoute,
        shippingAddress,
        namedDayError,
      }: {
        billingAddress: Address;
        cartEmptyError: boolean;
        cartItemsCount: number;
        currentRoute: IRouteDefinition;
        shippingAddress: Address;
        namedDayError: boolean;
      }) => {
        if (!cartItemsCount || cartEmptyError || namedDayError) {
          this.routes.redirect(ExternalRoute.CART);
          return;
        }
        const hasAddresses = !shippingAddress.isNewAddress || transaction?.isBillingAddressEligibleForCurrentPayment;
        if ((!hasAddresses || this.isPaymentAccessibilityRestricted) && currentRoute.includes(PAYMENT.ROUTE)) {
          this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE);
        }
      },
      {
        fireImmediately: true,
      },
    );
  }

  @computed
  private get isPaymentAccessibilityRestricted(): boolean {
    const errors = this.notificationsCollection;
    return errors.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_MILITARY)
      || errors.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS)
      || errors.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_POBOX)
      || errors.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_TERRITORY)
      || errors.hasNotification(RESTRICTION_ERRORS.RESTRICTION_OVERSEAS_TERRITORY)
      || errors.hasNotification(RESTRICTION_ERRORS.SHIPPING_UNAVAILABLE_FOR_ADDRESS);
  }

  @action
  private collectQueryParamsErrors(): void {
    const { queryParams } = this.stateRouter;

    Object.keys(queryParams).forEach((queryParamName: string) => {
      const ErrorKeys = Object.keys(queryParamsErrorsMapping) as string[];
      if (ErrorKeys.includes(queryParamName)) {
        const paramName = queryParamName as keyof typeof queryParamsErrorsMapping;
        
        let notificationGroup = NOTIFICATION_GROUP.APP;
        if (Object.keys(paymentQueryParamsErrorsMapping).includes(queryParamName)) {
          notificationGroup = NOTIFICATION_GROUP.PAYMENT;
        } else if (Object.keys(shippingQueryParamsErrorsMapping).includes(queryParamName)) {
          notificationGroup = NOTIFICATION_GROUP.SHIPPING;
        }

        this.notificationsCollection.addNotification({
          group: notificationGroup,
          key: queryParamsErrorsMapping[paramName],
          type: NOTIFICATION_TYPE.DANGER,
        });
      }
    });
  }

  @action
  private setTransaction(transaction: Transaction): void {
    this.transaction = transaction;
  }

  @action
  private setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  @computed
  get data(): IAppViewControllerData {
    const { config } = this.configRepository;
    return {
      isCountryUS: config && isCountryUS(config.countryId),
      isLoading: this.isLoading,
      translationSchema: this.translationRepository.translationSchema,
    };
  }

  private checkBrowserCompatibility = (): void => {
    if (/Trident\/|MSIE/.test(window.navigator.userAgent)) {
      setTimeout(() => {
        this.stateRouter.changeCurrentRoute(INCOMPATIBLE_BROWSER.ROUTE);
      }, TIMEOUT_INTERVAL.HUNDRED);
    }
  };

  @action
  setFocusBillingSection = (value: boolean): void => {
    this.focusBillingSection = value;
  };

  @action
  navigateToBilling = (): void => {
    this.setSameAsShipping(false);
    this.setFocusBillingSection(true);
    this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE);
  };

  @action
  navigateToStore = (focusTo: string): void => {
    this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE, {}, { bopis: focusTo });
  };

  @action
  showUpsWarningMessage = (value: boolean): void => {
    this.isWarningMessageActive = value;
  };

  @action
  showUpsAddressValidationModal = (value: boolean): void => {
    this.isUPSModalActive = value;
  };

  @action
  handleUpsSelect = async (
    selectedOption: string, deliveryOption: DeliveryOptions,
  ): Promise<void> => {
    try {
      const userAddress = Address.fromJSON(this.shippingRepository.userAddress);
      const upsAddress = Address.fromJSON(this.shippingRepository.upsAddress);
      let useCase;
      if (selectedOption === UPS_MODAL_OPTIONS.USE_RECOMMENDED) {
        useCase = new UseRecommendedUpsAddressUseCase(
          userAddress,
          upsAddress,
          this.isBillingSameAsShipping,
          deliveryOption,
        );
      }
    
      this.showUpsWarningMessage(false);
      await useCase?.execute();
    } catch (error) {
      this.showUpsWarningMessage(true);
      throw error;
    }
  };

  @action
  public setInitialSameAsShipping = (): void => {
    const { user: { isGuestUser, registered } } = this.transaction;
    if (isGuestUser
      && this.transaction
      && (this.transaction.shippingAddress.isNewAddress
      || this.transaction.isSameAsShipping)) {
      this.setSameAsShipping(true);
    } else if (registered) {
      if (
        this.transaction.shippingAddress.isNewAddress
        || this.transaction.billingAddress.isNewAddress
      ) {
        this.setSameAsShipping(true);
      } else {
        this.setSameAsShipping(this.transaction.isSameAsShipping);
      }
    }
  };

  @action
  public setInitialIsCompanyBilling = (): void => {
    const { canSetCompanyBilling } = this.configRepository.config;
    if (canSetCompanyBilling) {
      const { [FIELD_TYPE.BILL_TO_COMPANY]: billToCompany } = this.transaction.companyBillingDetails.attributes;
      this.setIsCompanyBilling(billToCompany);
    } else {
      this.setIsCompanyBilling(false);
    }
  };

  @action
  setSameAsShipping(sameAsShipping: boolean): void {
    this.isBillingSameAsShipping = sameAsShipping;
  }

  @action
  public toggleSameAsShipping = (): void => {
    const { user: { isGuestUser } } = this.transaction;
    if (!this.isBillingSameAsShipping && isGuestUser) {
      this.billingRepository.formStore.clearStore();
    }
    this.isBillingSameAsShipping = !this.isBillingSameAsShipping;
  };

  @action
  setIsCompanyBilling(isCompanyBilling: boolean): void {
    this.isCompanyBilling = isCompanyBilling;
  }

  @action toggleCompanyBilling = (): void => {
    this.isCompanyBilling = !this.isCompanyBilling;
  };

  onClickBack = (): void => {
    if (this.stateRouter.currentRoute === PAYMENT.ROUTE) {
      this.stateRouter.changeCurrentRoute(DELIVERY.ROUTE);
    } else if (this.stateRouter.currentRoute === DELIVERY.ROUTE) {
      this.navigateToViewCart();
    }
  };

  navigateToViewCart(): void {
    this.routes.redirect(ExternalRoute.CART);
  }

  private async setupSession(): Promise<void> {
    const transaction = await this.transactionRepository.getTransaction();
    const { isKeptSignedIn } = transaction.user;
    const { environment, sessionTimeout } = this.configRepository.config;

    if (environment === ENVIRONMENT_TYPE.PRODUCTION && !isKeptSignedIn) {
      this.session.init(sessionTimeout);
    }
  }
}
